/* eslint-disable max-classes-per-file */
import * as d3 from 'd3'
import { guff } from '../utils/guff'
import { binSize } from '../leaderboard/bat-speed-distribution-bin-size'

const colors = d3.schemeCategory10

const separator = '|'

const emptySummaryStats = {
    contact: 0,
    contact_percent: 0,
    hard_hit: 0,
    hard_hit_percent: 0,
    bbe: 0,
    bbe_percent: 0,
    is_competitive: 0,
    is_competitive_percent: 0,
    swings: 0,
    swings_percent: 0,
}

const defaultMinBucket = 46
const defaultMaxBucket = 90

class DistributionCurve {
    avgRadius = 20

    constructor(
        container,
        buckets,
        {
            minBucket = defaultMinBucket,
            maxBucket = defaultMaxBucket,
            bucketSize = binSize,
        } = {},
        idx = 0,
        totals = null,
        isDashed = false,
        key = ''
    ) {
        this.chart = container
        this.options = { minBucket, maxBucket, bucketSize, isDashed, idx }
        this.totals = totals

        this.allBuckets = fillInMissingBuckets(buckets, minBucket, maxBucket)
        this.curve = this.chart
            .append('path')
            .attr('stroke', isDashed ? '#666' : colors[idx])
            .attr('fill', isDashed ? 'none' : colors[idx])
            .attr('fill-opacity', 0.5)
            .attr('stroke-width', 4)
            .attr('stroke-opacity', 0.7)
            .attr('stroke-dasharray', isDashed ? '6' : '')
            .on('mouseover', () => handleMouseover(key))
            .on('mouseout', () => handleMouseout())

        this.setupAvgMarker()
    }

    destroy() {
        this.chart.remove()
    }

    updateCurve(prop, xScale, yScale) {
        this.chart.transition().style('opacity', 1)

        this.prop = prop
        this.xScale = xScale
        this.yScale = yScale

        this.addBufferPoints(prop)

        this.drawCurve(prop)
        this.drawAvgMarker(prop)
        // this.drawDebugPoints(prop)
    }

    hideCurve() {
        this.chart.transition().style('opacity', 0)
    }

    // We want the lines to always go to zero
    addBufferPoints(prop) {
        const { minBucket, maxBucket, bucketSize } = this.options

        const [bottomBucket, topBucket] = d3.extent(
            this.allBuckets
                .filter((bucket) => bucket[prop] > 0)
                .map((bucket) => bucket.bin)
        )

        // Add buffer point at bottom if necessary (should be)
        const bottomBufferBucket = Math.max(
            minBucket,
            bottomBucket - bucketSize / 2
        )

        const topBufferBucket = Math.min(maxBucket, topBucket + bucketSize / 2)

        this.buckets = this.allBuckets.map((bucket) => {
            if (bucket.bin < bottomBucket) {
                return { ...bucket, bin: bottomBufferBucket }
            }
            if (bucket.bin > topBucket) {
                return { ...bucket, bin: topBufferBucket }
            }

            return { ...bucket }
        })

        // Make sure there's always at least one buffer point on either end
        this.buckets = [
            {
                ...emptySummaryStats,
                bin: bottomBufferBucket,
            },
            ...this.buckets,
            {
                ...emptySummaryStats,
                bin: topBufferBucket,
            },
        ]
    }

    drawCurve(prop = 'swings') {
        const valueLine = d3
            .line()
            .curve(d3.curveBasis)
            .x(({ bin }) => this.xScale(bin))
            .y((d) => this.yScale(d[prop]))

        this.curve.transition().attr('d', valueLine(this.buckets))
    }

    drawAvgMarker(prop = 'swings') {
        if (!this.totals) {
            return
        }

        const newProp = prop.replace('_percent', '')
        const avg = this.totals[`${newProp}_avg`]
        this.avgMarker
            .transition()
            .attr('transform', `translate(${this.xScale(avg)}, 0)`)

        // Will stay the same, but this.yScale needs to exist first
        const simpleLine = d3.line()
        const nudge = 13
        this.avgMarkerPath.attr(
            'd',
            simpleLine([
                [0, this.yScale(0) + nudge],
                [0, this.yScale(0) - nudge],
            ])
        )

        this.avgMarkerCircleG.attr(
            'transform',
            `translate(0, ${this.yScale(0) - nudge - this.avgRadius})`
        )
        this.avgMarkerActualAverage.text(guff(avg, 'swing_speed'))
    }

    drawDebugPoints(prop) {
        this.chart
            .selectAll('circle')
            .data(this.buckets)
            .join(
                (enter) =>
                    enter
                        .append('circle')
                        .attr('cx', (d) => this.xScale(d.bin))
                        .attr('cy', (d) => this.yScale(d[prop]))
                        .attr('r', 5)
                        .attr('fill', '#666'),
                (update) => {
                    update
                        .transition()
                        .attr('cx', (d) => this.xScale(d.bin))
                        .attr('cy', (d) => this.yScale(d[prop]))

                    return update
                }
            )
    }

    setupAvgMarker() {
        if (!this.totals) {
            return
        }

        const { isDashed, idx } = this.options
        this.avgMarker = this.chart.append('g')

        this.avgMarkerPath = this.avgMarker
            .append('path')
            .attr('stroke', isDashed ? '#666' : colors[idx])
            .attr('fill', 'none')
            .attr('stroke-width', 4)
            .attr('stroke-opacity', 0.7)
            .attr('stroke-dasharray', isDashed ? '6' : '')

        this.avgMarkerCircleG = this.avgMarker
            .append('g')
            .attr('class', 'avg-circle-g')
            .attr('opacity', 0)

        this.avgMarkerCircle = this.avgMarkerCircleG
            .append('circle')
            .attr('r', 20)
            .attr('fill', 'rgba(255,255,255,0.9)')
            .attr('stroke-width', 2)
            .attr('stroke', isDashed ? '#666' : colors[idx])
            .attr('stroke-width', 2)
            .attr('stroke-opacity', 0.9)

        this.avgMarkerAvgText = this.avgMarkerCircleG
            .append('text')
            .attr('fill', '#999')
            .attr('text-anchor', 'middle')
            .attr('font-size', 9)
            .attr('dy', '1.45em')
            .text('AVG')

        this.avgMarkerActualAverage = this.avgMarkerCircleG
            .append('text')
            .attr('fill', isDashed ? '#666' : colors[idx])
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-weight', 'bold')
            .attr('dy', '0')
    }
}

// I can't quite bare to delete this
// class Histogram {
//     constructor(rawData, container, xScale, yScale, options, idx) {
//         // pixels on either side of the bar
//         this.barBuffer = 2
//         this.rawData = rawData
//         this.chart = container.append('g')
//         this.idx = idx
//         this.numHistograms = 1

//         this.xScale = xScale
//         this.yScale = yScale
//         this.options = options

//         this.parseData()
//         this.setupScales()
//         this.setupDom()

//         this.draw()
//     }

//     destroy() {
//         this.chart.remove()
//     }

//     setupDom() {
//         this.curve = this.chart
//             .append('path')
//             .attr('stroke', colors[this.idx])
//             .attr('fill', colors[this.idx])
//             .attr('fill-opacity', 0.3)
//             .attr('stroke', colors[this.idx])
//             .attr('stroke-width', 4)
//             .attr('stroke-opacity', 0.7)

//         this.curveBattedBallEvents = this.chart
//             .append('path')
//             .attr('stroke', colors[this.idx])
//             .attr('fill', colors[this.idx])
//             .attr('fill-opacity', 0.3)
//             .attr('stroke', colors[this.idx])
//             .attr('stroke-width', 4)
//             .attr('stroke-opacity', 0.7)

//         this.curveHardHit = this.chart
//             .append('path')
//             .attr('stroke', colors[this.idx])
//             .attr('fill', colors[this.idx])
//             .attr('fill-opacity', 0.5)
//             .attr('stroke', colors[this.idx])
//             .attr('stroke-width', 4)
//             .attr('stroke-opacity', 0.7)
//     }

//     setupScales() {
//         this.barWidth =
//             (this.xScale(this.options.bucketSize) -
//                 this.xScale(0) -
//                 2 * this.barBuffer) /
//             this.numHistograms

//         this.chart
//             .transition()
//             .attr(
//                 'transform',
//                 `translate(${this.barWidth * this.idx + this.barBuffer})`
//             )
//     }

//     updateYScale(yScale, idx, numHistograms) {
//         this.yScale = yScale

//         this.idx = idx
//         this.numHistograms = numHistograms

//         this.setupScales()

//         this.draw()
//     }

//     draw() {
//         this.drawCurve()
//         // this.drawHistogram()
//         this.debugPoints()
//     }

//     drawCurve() {
//         const { bucketSize } = this.options
//         const [minBucket, maxBucket] = this.extent

//         const valueLine = d3
//             .line()
//             .curve(d3.curveBasis)
//             .x(([bucket]) => this.xScale(Number(bucket) + bucketSize / 2))
//             .y(([, arr]) => this.yScale(arr.length))

//         const lineArr = [
//             [minBucket - bucketSize / 2, []],
//             ...Object.entries(this.buckets).sort(([keyA], [keyB]) =>
//                 Number(keyA) - Number(keyB) > 0 ? 1 : -1
//             ),
//             [
//                 Math.min(
//                     maxBucket + bucketSize / 2,
//                     this.options.maxBucket - bucketSize / 2
//                 ),
//                 [],
//             ],
//         ]
//         this.curve.transition().attr('d', valueLine(lineArr))

//         // const lineArrBbe = [
//         //     [minBucket - bucketSize / 2, []],
//         //     ...Object.entries(this.bucketsBattedBallEvents),
//         //     [maxBucket - bucketSize / 2, []],
//         // ]
//         // this.curveBattedBallEvents.transition().attr('d', valueLine(lineArrBbe))

//         // const lineArrHardHit = [
//         //     [minBucket - bucketSize / 2, []],
//         //     ...Object.entries(this.bucketsHardHitEvents),
//         //     [maxBucket - bucketSize / 2, []],
//         // ]
//         // this.curveHardHit.transition().attr('d', valueLine(lineArrHardHit))
//     }

//     drawHistogram() {
//         this.chart
//             .selectAll('g.histogram-bucket')
//             .data(Object.entries(this.buckets), ([bucket]) => bucket)
//             .join(
//                 (enter) => {
//                     const el = enter
//                         .append('g')
//                         .attr('class', 'histogram-bucket')

//                     // Full
//                     el.append('rect')
//                         .attr('class', ([bucket]) => `full bucket-${bucket}`)
//                         .attr('x', ([bucket]) => this.xScale(bucket))
//                         .attr('width', this.barWidth)
//                         .attr('y', ([, arr]) => this.yScale(arr.length))
//                         .attr(
//                             'height',
//                             ([, arr]) =>
//                                 this.yScale(0) - this.yScale(arr.length)
//                         )
//                         .attr('fill', colors[this.idx])
//                         .attr('opacity', 0.2)

//                     // BBEs
//                     el.append('rect')
//                         .attr('class', 'batted-ball-events')
//                         .attr('x', ([bucket]) => this.xScale(bucket))
//                         .attr('width', this.barWidth)
//                         .attr('y', ([, arr]) => {
//                             const bbes = arr.filter((d) => d.is_hit_into_play)
//                             return this.yScale(bbes.length)
//                         })
//                         .attr('height', ([, arr]) => {
//                             const bbes = arr.filter((d) => d.is_hit_into_play)
//                             return this.yScale(0) - this.yScale(bbes.length)
//                         })
//                         .attr('fill', colors[this.idx])
//                         .attr('opacity', 0.2)

//                     // Hard hit
//                     el.append('rect')
//                         .attr('class', 'hard-hit')
//                         .attr('width', this.barWidth)
//                         .attr(
//                             'height',
//                             ([, arr]) =>
//                                 this.yScale(0) -
//                                 this.yScale(
//                                     arr.filter(
//                                         (d) => d.api_h_launch_speed >= 95
//                                     ).length
//                                 )
//                         )
//                         .attr('x', ([bucket]) => this.xScale(bucket))
//                         .attr('y', ([, arr]) =>
//                             this.yScale(
//                                 arr.filter((d) => d.api_h_launch_speed >= 95)
//                                     .length
//                             )
//                         )
//                         .attr('fill', colors[this.idx])

//                         .attr('opacity', 0.2)

//                     return el
//                 },
//                 (update) => {
//                     update
//                         .select('.full')
//                         .transition()
//                         .attr('y', ([, arr]) => this.yScale(arr.length))
//                         .attr('width', this.barWidth)
//                         .attr(
//                             'height',
//                             ([, arr]) =>
//                                 this.yScale(0) - this.yScale(arr.length)
//                         )

//                     update
//                         .select('.batted-ball-events')
//                         .transition()
//                         .attr('y', ([, arr]) => {
//                             const bbes = arr.filter((d) => d.is_hit_into_play)
//                             return this.yScale(bbes.length)
//                         })
//                         .attr('width', this.barWidth)
//                         .attr('height', ([, arr]) => {
//                             const bbes = arr.filter((d) => d.is_hit_into_play)
//                             return this.yScale(0) - this.yScale(bbes.length)
//                         })

//                     update
//                         .select('.hard-hit')
//                         .transition()
//                         .attr('y', ([, arr]) => {
//                             const hard = arr.filter(
//                                 (d) => d.api_h_launch_speed >= 95
//                             )
//                             return this.yScale(hard.length)
//                         })
//                         .attr('width', this.barWidth)
//                         .attr('height', ([, arr]) => {
//                             const hard = arr.filter(
//                                 (d) => d.api_h_launch_speed >= 95
//                             )
//                             return this.yScale(0) - this.yScale(hard.length)
//                         })

//                     return update
//                 }
//             )
//     }

//     debugPoints() {
//         const { bucketSize } = this.options
//         const [minBucket, maxBucket] = this.extent
//         this.chart
//             .selectAll('circle.test')
//             .data([
//                 [minBucket - bucketSize / 2, []],
//                 ...Object.entries(this.buckets),
//                 [
//                     Math.min(
//                         maxBucket + bucketSize / 2,
//                         this.options.maxBucket - bucketSize / 2
//                     ),
//                     [],
//                 ],
//             ])
//             .join(
//                 (enter) =>
//                     enter
//                         .append('circle')
//                         .attr('class', 'test')
//                         .attr('cx', ([bucket]) =>
//                             this.xScale(Number(bucket) + bucketSize / 2)
//                         )
//                         .attr('cy', ([, arr]) => this.yScale(arr.length))
//                         .attr('r', 5)
//                         .attr('opacity', 0.3),
//                 (update) =>
//                     update
//                         .transition()
//                         .attr('cx', ([bucket]) =>
//                             this.xScale(Number(bucket) + bucketSize / 2)
//                         )
//                         .attr('cy', ([, arr]) => this.yScale(arr.length))
//             )
//     }

//     parseData() {
//         // Split into groups
//         this.data = this.rawData.filter(
//             (d) =>
//                 d.sweetspot_speed_mph >= this.options.minBucket &&
//                 d.sweetspot_speed_mph < this.options.maxBucket
//         )

//         this.buckets = this.getBuckets(this.data)
//         this.buckets = getTrimmedBuckets(this.buckets)

//         this.extent = d3.extent(Object.keys(this.buckets).map((d) => Number(d)))

//         this.battedBallEvents = this.data.filter((d) => d.is_hit_into_play)
//         this.bucketsBattedBallEvents = this.getBuckets(this.battedBallEvents)

//         this.hardHit = this.battedBallEvents.filter(
//             (d) => d.api_h_launch_speed >= 95
//         )
//         this.bucketsHardHitEvents = this.getBuckets(this.hardHit)
//     }

//     getBuckets(arr) {
//         const { minBucket, maxBucket, bucketSize } = this.options
//         const initBuckets = getInitialBuckets(minBucket, maxBucket, bucketSize)
//         return arr.reduce((acc, curr) => {
//             const bucket =
//                 Math.floor(
//                     (curr.sweetspot_speed_mph - minBucket) / bucketSize
//                 ) *
//                     bucketSize +
//                 minBucket

//             acc[bucket].push(curr)

//             return acc
//         }, initBuckets)
//     }
// }

const legendTypes = {
    batter: {
        displayFn: (d) => d.title,
        subTitleFn: (d) => {
            if (d.dashed) {
                return `(${d.year})`
            }

            let side = 'Left + Right'

            if (d.side === 'L' || d.side === 'R') {
                side = d.side === 'L' ? 'Left' : 'Right'
            }
            return `(${d.year} - ${side})`
        },
    },
    season: {
        displayFn: (d) => {
            if (d.dashed) {
                return 'League Average'
            }
            return d.year
        },
        subTitleFn: () => '',
    },
    seasonSwitch: {
        displayFn: (d) => {
            if (d.dashed) {
                return 'League Average'
            }
            return d.year
        },
        subTitleFn: (d) => {
            if (d.dashed) {
                return ''
            }
            return d.side === 'L' ? '(Left)' : '(Right)'
        },
    },
}

export function createKey(playerId, year, side, playerType = '') {
    return `${playerId}-${year}-${side}-${playerType}`
}

function parseKey(key) {
    const [playerId, year, side, playerType = ''] = key.split('-')

    return { playerId, year, side, playerType }
}

function handleMouseover(key, $legend) {
    if ($legend) {
        $legend.select('.legend-close-icon').attr('opacity', 1)
    }

    d3.selectAll('g.distribution-curve').attr('opacity', 0.15)

    d3.select(`g.distribution-curve-${key}`)
        .attr('opacity', 1)
        .raise()
        .select('.avg-circle-g')
        .attr('opacity', 1)
}

function handleMouseout() {
    d3.selectAll('.legend-close-icon').attr('opacity', 0)

    d3.selectAll('g.distribution-curve')
        .attr('opacity', 1)
        .select('.avg-circle-g')
        .attr('opacity', 0)
}

class Legend {
    type = 'batter'

    constructor(chart, data, options = {}) {
        const { type = 'batter', editable = false, photo = true } = options
        this.chart = chart
        this.legend = chart.chart
            .append('g')
            .attr('transform', 'translate(0, 10)')

        this.type = type
        this.editable = editable
        this.photo = photo

        this.updateLegend(data)
    }

    updateLegend(data) {
        this.data = data

        const rectSize = this.photo ? 40 : 20
        this.legend
            .selectAll('g.legend')
            .data(data, (d) => d.key)
            .join(
                (enter) => {
                    const g = enter
                        .append('g')
                        .attr('class', 'legend')
                        .attr(
                            'transform',
                            (d, i) => `translate(10, ${i * (rectSize + 10)})`
                        )
                        .on('mouseover', function handler(_e, d) {
                            handleMouseover(d.key, d3.select(this))
                        })
                        .on('mouseout', handleMouseout)

                    g.append('rect')
                        // .attr('opacity', (d) => (d.dashed ? 0 : 1))
                        .attr('height', rectSize)
                        .attr('width', rectSize)
                        .attr('rx', 12)
                        .attr('stroke', (d) => {
                            if (d.dashed && this.photo) {
                                return 'none'
                            }
                            return d.dashed ? '#666' : colors[d.colorIdx]
                        })
                        .attr('fill', (d) =>
                            d.dashed ? '#c2c2c2' : colors[d.colorIdx]
                        )
                        .attr('fill-opacity', 0.3)
                        .attr('stroke-width', 4)
                        .attr('stroke-opacity', 0.7)
                        .attr('stroke-dasharray', (d) =>
                            d.dashed ? '3, 3' : undefined
                        )
                    // Test
                    const nudgeUp = 10
                    const points = [
                        [0, 40 - nudgeUp],
                        [5, 39 - nudgeUp],
                        [10, 36 - nudgeUp],
                        [15, 25 - nudgeUp],
                        [20, 15 - nudgeUp],
                        [25, 25 - nudgeUp],
                        [30, 36 - nudgeUp],
                        [35, 39 - nudgeUp],
                        [40, 40 - nudgeUp],
                    ]
                    const valueLine = d3.line().curve(d3.curveBasis)

                    const path = g
                        .append('path')
                        .attr('opacity', (d) =>
                            d.dashed && this.photo ? 1 : 0
                        )
                        .attr('fill', 'none')
                        .attr('stroke', '#666')
                        .attr('stroke-opacity', 0.7)
                        .attr('stroke-width', 3)
                        .attr('stroke-dasharray', '3, 3')

                    path.attr('d', valueLine(points))

                    if (this.photo) {
                        g.append('image')
                            .style('display', (d) =>
                                d.dashed ? 'none' : undefined
                            )
                            .attr('href', (d) =>
                                d.dashed
                                    ? `/site-core/images/savant-mlb.svg`
                                    : `https://content.mlb.com/images/headshots/current/60x60/${d.playerId}.png`
                            )
                            .attr('height', 30)
                            .attr('width', 30)
                            .attr('x', 5)
                            .attr('y', 5)
                    }

                    const textFn = legendTypes[this.type].displayFn
                    const { subTitleFn } = legendTypes[this.type]

                    g.append('text')
                        .text(textFn)
                        .attr('font-size', 18)
                        .attr('x', rectSize + 5)
                        .attr('y', rectSize / 2)
                        .attr('dominant-baseline', 'middle')
                        .append('tspan')
                        // .text((d) => {
                        //     const side = d.side === 'L' ? 'Left' : 'Right'
                        //     return `(${d.year} ${side})`
                        // })
                        .text(subTitleFn)
                        .attr('font-size', 12)
                        .attr('dx', 4)
                        .attr('dy', -1)

                    if (this.editable) {
                        const closeIcon = g
                            .append('g')
                            .attr('class', 'legend-close-icon')
                            .attr('opacity', 0)
                            .on('click', (_e, d) => {
                                if (d.dashed) {
                                    this.chart.removeDashedCurve(d.key)
                                } else {
                                    this.chart.removeCurve(d.key)
                                }
                                this.chart.updateUrl()
                            })

                        closeIcon
                            .append('circle')
                            .attr('cx', rectSize)
                            .attr('cy', 5)
                            .attr('r', 12)
                            .attr('fill', 'white')
                            .attr('fill-opacity', 0.8)

                        // .attr('stroke', '#666')
                        // .attr('stroke-width', 2)
                        closeIcon
                            .append('text')
                            .text('❌')
                            .attr('font-size', 10)
                            .attr('x', rectSize)
                            .attr('y', 5)
                            .attr('dy', 2)
                            .attr('dominant-baseline', 'middle')
                            .attr('text-anchor', 'middle')
                    }

                    return g
                },
                (update) => {
                    update
                        .transition()
                        .attr(
                            'transform',
                            (d, i) => `translate(10, ${i * (rectSize + 10)})`
                        )

                    return update
                }
            )
    }
}
export default class Chart {
    defaultOptions = {
        minBucket: defaultMinBucket,
        // Update comment, this is untrue. This is actually the end of the last bucket, i.e. 120 would mean the last bucket might be [115, 120)
        maxBucket: defaultMaxBucket,
        bucketSize: binSize,
        maxCurves: 4,
        legend: {
            editable: false,
            type: 'batter',
            photo: true,
        },
    }

    dims = {
        margin: {
            top: 30,
            right: 60,
            bottom: 60,
            left: 60,
        },
        height: 400,
        width: 800,
    }

    scaleType = 'percent'

    lockScale = true

    props = [
        { value: 'swings', label: 'Swings' },
        { value: 'contact', label: 'Contact' },
        { value: 'bbe', label: 'Hit Into Play' },
        { value: 'hard_hit', label: 'Hard Hit' },
        // { value: 'isCompetitive', label: 'Is Competitive' },
    ]

    percentProps = [
        { value: 'swings_percent', label: 'Swing %' },
        { value: 'contact_percent', label: 'Contact %' },
        { value: 'bbe_percent', label: 'Hit Into Play %' },
        { value: 'hard_hit_percent', label: 'Hard Hit %' },
        // { value: 'isCompetitivePercent', label: 'Is Competitive %' },
    ]

    prop = 'swings_percent'

    subscriptions = []

    constructor(el, initialData = [], options = {}) {
        this.wrapper = el

        this.svg = el
            .append('svg')
            .attr('class', 'bat-tracking-visual')
            .attr('height', this.dims.height)
            .attr('width', this.dims.width)

        // Multilayered options here
        // eslint-disable-next-line no-param-reassign
        options.legend = { ...this.defaultOptions.legend, ...options.legend }
        this.options = { ...this.defaultOptions, ...options }

        this.legendType = this.options.legend.type

        this.buildSelects()
        this.updateSelect()

        this.chart = this.svg
            .append('g')
            .attr(
                'transform',
                `translate(${this.dims.margin.left}, ${this.dims.margin.top})`
            )
        this.data = []
        this.store = {}
        // Optional dashed data, generally league average
        this.dashedData = []
        this.dashedStore = {}

        this.parseDims()
        this.updateScales()
        this.drawBackground()
        this.drawAxes()

        this.histograms = {}
        this.legend = new Legend(this, this.data, this.options.legend)

        initialData.forEach(({ bins, totals, ...extras }) => {
            this.addCurve(extras, { bins, totals })
        })

        this.drawCurves()
    }

    buildSelects() {
        // Three different selects
        this.selectWrapper = this.wrapper
            .append('div')
            .attr('class', 'bat-tracking-dist-select-wrapper')

        this.lockSelect = this.createDropdown('lock-select', 'Lock Scales', [
            { value: '1', text: 'Yes ' },
            { value: '0', text: 'No ' },
        ]).on('change', (e) => {
            this.updateLock(e.target.value === '1')
        })

        this.scaleSelect = this.createDropdown('scale-select', 'Scale', [
            { value: 'percent', text: '% Total Swings ' },
            { value: 'raw', text: 'Raw Swing Totals ' },
        ]).on('change', (e) => {
            this.updateScaleType(e.target.value)
            this.updateLegend()
        })

        this.distSelect = this.createDropdown('dist-select', '', []).on(
            'change',
            (e) => {
                this.updateProp(e.target.value)
            }
        )
    }

    // There's a little more involved HTML to get the styled dropdown. Web component?
    createDropdown(id, title, options) {
        const wrap = this.selectWrapper
            .append('div')
            .attr('class', 'savant-select')
            .attr('data-title', title || undefined)
            .style('margin', '0 0.2rem')

        const select = wrap.append('select').attr('class', id)

        select
            .selectAll('option')
            .data(options)
            .join((enter) =>
                enter.append('option').attr('value', (d) => d.value)
            )
            .text((d) => d.text)

        return select
    }

    addCurve(options, { bins, totals }) {
        const { key, playerId, title, playerType } = options

        if (this.store[key]) {
            return
        }

        const { year, side } = parseKey(key)

        if (this.data.length >= this.options.maxCurves) {
            this.removeCurve(this.data[0].key)
        }

        const colorIdx = this.getNextColor()
        this.data.push({
            key,
            playerId,
            playerType,
            title,
            bins,
            colorIdx,
            year,
            side,
            options,
            totals,
            dashed: false,
        })

        this.updateLegend()
        this.addCurveData(key, bins, colorIdx, totals)
        this.drawCurves()
    }

    addDashed(options, { bins, totals }) {
        const { key, playerId, title } = options

        if (this.dashedStore[key]) {
            return
        }

        const { year, side } = parseKey(key)

        this.dashedData.push({
            key,
            playerId,
            title,
            bins,
            colorIdx: 100,
            year,
            side,
            options,
            totals,
            dashed: true,
        })

        this.updateLegend()
        this.addDashedCurveData(key, bins, totals)
        this.drawCurves()
    }

    updateLegend() {
        const legendData =
            this.scaleType === 'percent'
                ? [...this.data, ...this.dashedData]
                : //   [...this.dashedData, ...this.data]
                  this.data
        this.legend.updateLegend(legendData)
    }

    getNextColor() {
        let i = 0
        const colorIndices = new Set(this.data.map((d) => d.colorIdx))
        while (colorIndices.has(i)) {
            i += 1
        }

        return i
    }

    addCurveData(key, bins, colorIdx, totals) {
        const { minBucket, maxBucket } = this.options
        const filteredBuckets = bins.filter(
            (d) => d.bin >= minBucket && d.bin < maxBucket
        )

        const container = this.chart
            .append('g')
            .attr('class', `distribution-curve distribution-curve-${key}`)

        const curve = new DistributionCurve(
            container,
            filteredBuckets,
            this.options,
            colorIdx,
            totals,
            false,
            key
        )
        this.store[key] = { bucketsArr: filteredBuckets, curve }
    }

    addDashedCurveData(key, bins, totals) {
        const { minBucket, maxBucket } = this.options
        const filteredBuckets = bins.filter(
            (d) => d.bin >= minBucket && d.bin < maxBucket
        )

        const container = this.chart
            .append('g')
            .attr('class', `distribution-curve distribution-curve-${key}`)

        const curve = new DistributionCurve(
            container,
            filteredBuckets,
            this.options,
            100,
            totals,
            true,
            key
        )
        this.dashedStore[key] = { bucketsArr: filteredBuckets, curve }
    }

    removeCurve(key) {
        const { curve } = this.store[key] || {}

        if (!curve) {
            return
        }

        curve.destroy()
        delete this.store[key]
        this.data = this.data.filter((d) => d.key !== key)

        this.drawCurves()
        this.updateLegend()
    }

    removeDashedCurve(key) {
        const { curve } = this.dashedStore[key] || {}

        if (!curve) {
            return
        }

        curve.destroy()
        delete this.store[key]
        this.dashedData = this.dashedData.filter((d) => d.key !== key)

        this.drawCurves()
        this.updateLegend()
    }

    updateSelect() {
        const options =
            this.scaleType === 'percent' ? this.percentProps : this.props
        this.distSelect
            .selectAll('option')
            .data(options)
            .join(
                (enter) =>
                    enter
                        .append('option')
                        .text((d) => d.label)
                        .attr('value', (d) => d.value),
                (update) =>
                    update.text((d) => d.label).attr('value', (d) => d.value)
            )
    }

    updateProp(prop) {
        this.prop = prop
        this.drawCurves()
        this.distSelect.property('value', prop)
        this.publish('prop')
    }

    drawCurves() {
        this.updateScales()
        this.updateAxes()

        Object.values(this.store).forEach(({ curve }) => {
            curve.updateCurve(this.prop, this.xScale, this.yScale)
        })

        Object.values(this.dashedStore).forEach(({ curve }) => {
            if (this.scaleType === 'percent') {
                curve.updateCurve(this.prop, this.xScale, this.yScale)
            } else {
                curve.hideCurve()
            }
        })
    }

    updateUrl() {
        const playerIds = this.data.map((d) => d.playerId)
        const years = this.data.map((d) => d.year)
        const sides = this.data.map((d) => d.side || '')
        const playerTypes = this.data.map((d) => d.playerType || '')
        const { lockScale, prop, scaleType } = this

        const url = new URL(window.location)
        url.searchParams.set('playerIds', playerIds.join(separator))
        url.searchParams.set('years', years.join(separator))
        url.searchParams.set('sides', sides.join(separator))
        url.searchParams.set('playerTypes', playerTypes.join(separator))
        url.searchParams.set('lockScale', lockScale ? 1 : 0)
        url.searchParams.set('scaleType', scaleType)
        url.searchParams.set('yAxis', prop)

        window.history.replaceState('', '', url.toString())
    }

    updateScaleType(prop) {
        this.scaleType = prop === 'percent' ? 'percent' : 'raw'
        this.updateSelect()

        if (this.scaleType === 'percent') {
            const newProp = `${this.prop.replace('_percent', '')}_percent`
            this.updateProp(newProp, true)
        } else {
            const newProp = this.prop.replace('_percent', '')
            this.updateProp(newProp, true)
        }
        this.publish('scale')
        this.scaleSelect.property('value', prop)
    }

    updateLock(lockScale) {
        this.lockScale = lockScale
        this.drawCurves()
        this.publish('lock')
        this.lockSelect.property('value', lockScale ? '1' : '0')
    }

    subscribe(fn) {
        this.subscriptions.push(fn)
    }

    unsubscribe(fn) {
        this.subscriptions = this.subscriptions.filter(
            (listener) => listener !== fn
        )
    }

    publish(type) {
        this.subscriptions.forEach((listener) => {
            listener(type, this)
        })
    }

    getYMax() {
        if (!this.data.length && !this.yMax) {
            this.yMax = this.scaleType === 'percent' ? 0.25 : 100
            return
        }
        if (!this.data.length) {
            return
        }

        let { prop } = this
        if (this.lockScale) {
            prop = this.scaleType === 'raw' ? 'swings' : 'swings_percent'
        }

        let maxes = this.data.map(({ bins }) => d3.max(bins, (d) => d[prop]))

        if (this.scaleType === 'percent') {
            const dashedMaxes = this.dashedData.map(({ bins }) =>
                d3.max(bins, (d) => d[prop])
            )
            maxes = [...maxes, dashedMaxes]
        }

        this.yMax = Math.max(...maxes)
    }

    parseDims() {
        const { height, width, margin } = this.dims
        this.dims.chartHeight = height - margin.top - margin.bottom
        this.dims.chartWidth = width - margin.left - margin.right
    }

    updateScales() {
        this.xScale = d3
            .scaleLinear()
            .domain([this.options.minBucket, this.options.maxBucket])
            .range([0, this.dims.chartWidth])

        this.barWidth =
            (this.xScale(this.options.bucketSize) - this.xScale(0)) /
            this.numHistograms

        this.getYMax()

        this.yScale = d3
            .scaleLinear()
            .domain([this.yMax, 0])
            .range([0, this.dims.chartHeight])
            .nice()
    }

    getTickFormat() {
        switch (this.prop) {
            case 'swings_percent':
            case 'hard_hit_percent':
            case 'bbe_percent':
            case 'contact_percent':
                return d3.format('.0%')
            default:
                return undefined
        }
    }

    drawAxes() {
        const xAxis = d3
            .axisBottom(this.xScale)
            .tickSize(-1 * this.dims.chartHeight)
        const yAxis = d3
            .axisLeft(this.yScale)
            .tickFormat(this.getTickFormat())
            .tickSize(-1 * this.dims.chartWidth)

        this.xAxisEl = this.chart
            .append('g')
            .attr('class', 'axis x-axis')
            .attr('transform', `translate(0, ${this.dims.chartHeight})`)
            .call(xAxis)
            .call((el) => {
                el.selectAll('.tick line')
                    .attr('stroke-opacity', 0.2)
                    .attr('stroke-dasharray', '5,5')

                // el.selectAll('.tick:first-of-type text').remove()
                el.selectAll('.tick:last-of-type text').remove()
            })

        this.yAxisEl = this.chart
            .append('g')
            .attr('class', 'axis y-axis')
            .call(yAxis)
            .call((el) => {
                el.selectAll('.tick line')
                    .attr('stroke-opacity', 0.2)
                    .attr('stroke-dasharray', '5,5')
            })

        this.chart
            .append('text')
            .attr('class', 'xLabel')
            .attr(
                'transform',
                `translate(${this.dims.chartWidth / 2} ,${
                    this.dims.chartHeight + 35
                })`
            )
            .style('text-anchor', 'middle')
            .style('font-size', '1rem')
            .text('Bat Speed')

        this.chart
            .append('text')
            .attr('class', 'yLabel')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - this.dims.margin.left / 2 - 7)
            .attr('x', 0 - this.dims.chartHeight / 2)
            .attr('dy', '0em')
            .style('text-anchor', 'middle')
            .style('font-size', '1rem')
            .text('Swings')
    }

    drawBackground() {
        this.chart
            .append('rect')
            .attr('fill', 'white')
            .attr('height', this.dims.chartHeight)
            .attr('width', this.dims.chartWidth)
    }

    updateAxes() {
        const height = this.yScale.range()[1]
        const xAxis = d3.axisBottom(this.xScale).tickSize(-1 * height)
        const yAxis = d3
            .axisLeft(this.yScale)
            .tickFormat(this.getTickFormat())
            .tickSize(-1 * this.dims.chartWidth)

        this.xAxisEl.transition().call(xAxis)
        this.yAxisEl
            .transition()
            .call(yAxis)
            .call((el) => {
                el.selectAll('.tick line')
                    .attr('stroke-opacity', 0.2)
                    .attr('stroke-dasharray', '5,5')
            })
    }

    removeData(key) {
        this.histograms[key]?.histogram.destroy()

        delete this.data[key]
        delete this.histograms[key]

        this.updateScales()
        this.updateAxes()

        this.updateHistograms()
    }

    updateHistograms() {
        Object.values(this.histograms).forEach(({ histogram }, i, arr) => {
            histogram.updateYScale(this.yScale, i, arr.length)
        })
    }
}

function fillInMissingBuckets(buckets, minBucket, maxBucket) {
    // Sort to be careful.
    buckets.sort((a, b) => (a.bin < b.bin ? -1 : 1))

    const allBuckets = []
    let idx = 0

    for (let bucket = minBucket; bucket <= maxBucket; bucket += binSize) {
        if (buckets[idx]?.bin === bucket) {
            allBuckets.push(buckets[idx])
            idx += 1
        } else {
            allBuckets.push({ ...emptySummaryStats, bin: bucket })
        }
    }

    return allBuckets
}
